import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { useFieldValue } from "formik";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { combinePaths } from "../models/flexible/utils";
import { Brand, Preparation } from "../models/generated/graphql";
import { getScopePreparations } from "../utils/agreement-utils";
import { anyNamedEntity } from "../utils/any-named-entity";
import { distinctBy } from "../utils/distinct-by";
import { getPathPrefix } from "../utils/get-path-prefix";
import { useScopeFields } from "./useScopeFields";
import { useScopes } from "./useScopes";

export const useAvailableBrandNames = (fieldController: FlexFieldController, path: string) => {
	const [value] = useFieldValue(path);
	const [genericValue] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.GenericName));
	const [brandValues] = useFieldValue(combinePaths(getPathPrefix(path, 1), AttributeIdentifiers.BrandName));
	const { multiBrandSets, unfilteredGenerics } = useScopeFields(fieldController.parent.context);
	const { selectedScopes, allScopes } = useScopes(fieldController.parent.context);
	if (!allScopes.length && multiBrandSets.length) {
		return multiBrandSets;
	}

	const isAnyAvailable =
		selectedScopes
			.flatMap((scope) => scope.treatmentSets)
			.filter((treatmentSet) => !genericValue || genericValue.Name === treatmentSet.generic.Name)
			.some(
				(treatmentSet) =>
					treatmentSet.preparations.length === 0 ||
					(getDistinctBrandNamesFromPreparations(treatmentSet.preparations).length > 1 && unfilteredGenerics.length > 1)
			) ||
		(!!genericValue && brandValues.length === 0);

	const availableBrands = selectedScopes
		.flatMap((scope) => getScopePreparations(scope, (treatmentSet) => !genericValue || treatmentSet.generic.Name === genericValue.Name))
		.map((p) => p.brand)
		.filter((brand) =>
			multiBrandSets.every(
				(selectedBrandSet) =>
					!selectedBrandSet.some((selectedBrand) => selectedBrand.Name === brand.Name) ||
					value?.some((brandValue: Brand) => brandValue.Name === brand.Name)
			)
		);

	const extendedBrandOptions = isAnyAvailable ? [anyNamedEntity as Brand, ...availableBrands] : availableBrands;
	return distinctBy(extendedBrandOptions, (brand) => brand.Name).sort(
		sortBySelectors(
			(brand) => brand.__typename,
			(brand) => brand.Name
		)
	);
};

const getDistinctBrandNamesFromPreparations = (preparations: Array<Preparation>) => {
	return distinctBy(
		preparations.flatMap((preparation) => preparation.BrandName),
		(brandName) => brandName
	);
};
